import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { salesAgentsAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Users, Plus, Edit, Trash2, Loader2, UserPlus, Building2, Search, Download,
} from "lucide-react";

export default function SalesAgents() {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", commission_rate: 10, type: "external",
    linked_user_id: "", company: "", notes: "",
  });

  const [catalogEmpty, setCatalogEmpty] = useState(false);

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (showInactive) params.include_inactive = true;
      if (typeFilter !== "all") params.type_filter = typeFilter;
      const res = await salesAgentsAPI.getAll(params);
      const data = res.data;
      if (data && data.error === "CATALOG_EMPTY") {
        setAgents([]);
        setCatalogEmpty(true);
      } else {
        setAgents(Array.isArray(data) ? data : []);
        setCatalogEmpty(false);
      }
    } catch (err) {
      toast.error("Error al cargar agentes de venta");
    } finally {
      setLoading(false);
    }
  }, [showInactive, typeFilter]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const openCreateDialog = () => {
    setEditingAgent(null);
    setFormData({ name: "", phone: "", email: "", commission_rate: 10, type: "external", linked_user_id: "", company: "", notes: "" });
    setDialogOpen(true);
  };

  const openEditDialog = (agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name || "",
      phone: agent.phone || "",
      email: agent.email || "",
      commission_rate: agent.commission_rate ?? 10,
      type: agent.type || "external",
      linked_user_id: agent.linked_user_id || "",
      company: agent.company || "",
      notes: agent.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error("Nombre es obligatorio"); return; }
    if (formData.commission_rate < 0 || formData.commission_rate > 100) { toast.error("Tasa de comisión debe ser entre 0 y 100"); return; }

    setSaving(true);
    try {
      if (editingAgent) {
        await salesAgentsAPI.update(editingAgent.id, {
          name: formData.name,
          phone: formData.phone || null,
          email: formData.email || null,
          commission_rate: parseFloat(formData.commission_rate),
          company: formData.company || null,
          notes: formData.notes || null,
        });
        toast.success("Agente actualizado");
      } else {
        await salesAgentsAPI.create({
          ...formData,
          commission_rate: parseFloat(formData.commission_rate),
          phone: formData.phone || null,
          email: formData.email || null,
          company: formData.company || null,
          notes: formData.notes || null,
          linked_user_id: formData.type === "internal" ? formData.linked_user_id : null,
        });
        toast.success("Agente creado exitosamente");
      }
      setDialogOpen(false);
      fetchAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar agente");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (agent) => {
    if (!window.confirm(`¿Desactivar a ${agent.name}?`)) return;
    try {
      await salesAgentsAPI.delete(agent.id);
      toast.success("Agente desactivado");
      fetchAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al desactivar agente");
    }
  };

  const handleSeed = async () => {
    if (!window.confirm("Esto creará registros de agentes para todos los usuarios vendedores que aún no tengan uno. ¿Continuar?")) return;
    setSeeding(true);
    try {
      const res = await salesAgentsAPI.seedFromUsers();
      toast.success(`${res.data.message}`);
      fetchAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en seed");
    } finally {
      setSeeding(false);
    }
  };

  const filtered = agents.filter(a => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (a.name || "").toLowerCase().includes(term) ||
      (a.email || "").toLowerCase().includes(term) ||
      (a.company || "").toLowerCase().includes(term) ||
      (a.phone || "").includes(term);
  });

  const stats = {
    total: agents.length,
    internal: agents.filter(a => a.type === "internal").length,
    external: agents.filter(a => a.type === "external").length,
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="sales-agents-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Agentes de Venta</h1>
            <p className="text-sm text-slate-500 mt-1">Catálogo de vendedores comisionables (internos y externos)</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding} data-testid="seed-agents-btn">
              {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
              Sincronizar Internos
            </Button>
            <Button onClick={openCreateDialog} data-testid="create-agent-btn">
              <Plus className="h-4 w-4 mr-1" /> Nuevo Agente
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Total Agentes</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-total">{stats.total}</p>
                </div>
                <Users className="h-8 w-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Internos</p>
                  <p className="text-2xl font-bold text-blue-600" data-testid="stat-internal">{stats.internal}</p>
                </div>
                <UserPlus className="h-8 w-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Externos</p>
                  <p className="text-2xl font-bold text-amber-600" data-testid="stat-external">{stats.external}</p>
                </div>
                <Building2 className="h-8 w-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                <Search className="h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, email, empresa..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-8"
                  data-testid="search-agents-input"
                />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[160px] h-8" data-testid="type-filter">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="internal">Internos</SelectItem>
                  <SelectItem value="external">Externos</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="show-inactive-toggle" />
                <Label className="text-xs text-slate-500">Ver inactivos</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-slate-500" data-testid="empty-state">
                <Users className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                {catalogEmpty ? (
                  <>
                    <p className="text-sm font-medium text-red-600">Catálogo de agentes no inicializado</p>
                    <p className="text-xs mt-1 max-w-md mx-auto">Haga clic en "Sincronizar Internos" para crear automáticamente los registros de agentes a partir de los vendedores del sistema. Luego puede agregar agentes externos manualmente.</p>
                    <Button variant="default" size="sm" className="mt-4" onClick={handleSeed} disabled={seeding} data-testid="seed-empty-btn">
                      {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
                      Sincronizar Internos Ahora
                    </Button>
                  </>
                ) : (
                  <p className="text-sm">No se encontraron agentes con los filtros actuales.</p>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead className="text-right">Comisión %</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((agent) => (
                    <TableRow key={agent.id} data-testid={`agent-row-${agent.id}`}>
                      <TableCell className="font-medium">{agent.name}</TableCell>
                      <TableCell>
                        <Badge variant={agent.type === "internal" ? "default" : "secondary"} data-testid={`agent-type-${agent.id}`}>
                          {agent.type === "internal" ? "Interno" : "Externo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500">{agent.company || "-"}</TableCell>
                      <TableCell>
                        <div className="text-xs">
                          {agent.phone && <div>{agent.phone}</div>}
                          {agent.email && <div className="text-slate-400">{agent.email}</div>}
                          {!agent.phone && !agent.email && <span className="text-slate-300">-</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">{agent.commission_rate}%</TableCell>
                      <TableCell>
                        <Badge variant={agent.active !== false ? "outline" : "destructive"} className={agent.active !== false ? "border-green-300 text-green-700" : ""}>
                          {agent.active !== false ? "Activo" : "Inactivo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDialog(agent)} data-testid={`edit-agent-${agent.id}`}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          {agent.active !== false && user?.role === "admin" && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(agent)} data-testid={`delete-agent-${agent.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>{editingAgent ? "Editar Agente" : "Nuevo Agente de Venta"}</DialogTitle>
              <DialogDescription>
                {editingAgent ? "Modifica los datos del agente" : "Registra un nuevo vendedor comisionable"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {!editingAgent && (
                <div className="space-y-1.5">
                  <Label>Tipo</Label>
                  <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                    <SelectTrigger data-testid="form-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="external">Externo (sin login al sistema)</SelectItem>
                      <SelectItem value="internal">Interno (usuario existente)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Nombre *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Nombre completo o razón social" data-testid="form-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Teléfono</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+52 33..." data-testid="form-phone-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="correo@ejemplo.com" data-testid="form-email-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Comisión (%)*</Label>
                  <Input type="number" min={0} max={100} step={0.5} value={formData.commission_rate} onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })} data-testid="form-commission-input" />
                </div>
                {formData.type === "external" && (
                  <div className="space-y-1.5">
                    <Label>Empresa</Label>
                    <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} placeholder="Hotel, agencia..." data-testid="form-company-input" />
                  </div>
                )}
              </div>
              {formData.type === "internal" && !editingAgent && (
                <div className="space-y-1.5">
                  <Label>ID de Usuario vinculado</Label>
                  <Input value={formData.linked_user_id} onChange={(e) => setFormData({ ...formData, linked_user_id: e.target.value })} placeholder="UUID del usuario del sistema" data-testid="form-linked-user-input" />
                  <p className="text-xs text-slate-400">Usa "Sincronizar Internos" para crear automáticamente desde usuarios existentes</p>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Notas</Label>
                <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Notas internas..." data-testid="form-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-agent-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {editingAgent ? "Guardar Cambios" : "Crear Agente"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
